import { useEffect, useState } from "react";
import { http } from "../api";
import { Trophy, Sparkles, Clock, Star } from "lucide-react";
import { PageHeader, EmptyState } from "../shared";

const PERIODS = [
  { id: "daily", label: "Today" },
  { id: "weekly", label: "This week" },
  { id: "monthly", label: "This month" },
  { id: "all", label: "All time" },
];

const MEDAL = ["🥇", "🥈", "🥉", "④", "⑤", "⑥"];

export default function Leaderboard({ user }) {
  const [period, setPeriod] = useState("weekly");
  const [top, setTop] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [board, self] = await Promise.all([
        http.get(`/rewards/leaderboard?period=${period}&limit=6`),
        http.get("/rewards/me"),
      ]);
      setTop(board.data.top || []);
      setMe(self.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]);

  return (
    <>
      <PageHeader eyebrow="COMMUNITY REWARDS"
        title="Leaderboard"
        subtitle="Every verified voter, resolved complaint and quality note earns points. Top 6 contributors show up here." />

      <section className="panel">
        <div className="lb-toolbar" data-testid="lb-toolbar">
          <div className="lb-chips">
            {PERIODS.map((p) => (
              <button key={p.id} className={`lb-chip ${period === p.id ? "on" : ""}`} data-testid={`lb-chip-${p.id}`} onClick={() => setPeriod(p.id)}>
                {p.label}
              </button>
            ))}
          </div>
          {me && (
            <div className="lb-me" data-testid="lb-me">
              <Star size={14} /> Your points: <b>{me.total || 0}</b> · <small>{me.actions || 0} actions</small>
              {(me.badges || []).length > 0 && (
                <span className="lb-me-badges" data-testid="lb-me-badges">
                  {(me.badges || []).map((b) => (
                    <span key={b.id} className="lb-badge lb-badge-me" title={b.hint || b.label} data-testid={`lb-me-badge-${b.id}`}>
                      <span className="lb-badge-emoji">{b.emoji}</span>{b.label}
                    </span>
                  ))}
                </span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <p className="hint" style={{ padding: 30, textAlign: "center" }}>Loading leaderboard…</p>
        ) : top.length === 0 ? (
          <EmptyState icon={Trophy} title="No contributors yet in this window" hint="Verify a voter, resolve a complaint or add a note to appear on the board." />
        ) : (
          <ol className="lb-list" data-testid="lb-list">
            {top.map((u, i) => (
              <li key={u.user_id} className={`lb-row lb-rank-${i}`} data-testid={`lb-row-${i}`}>
                <span className="lb-medal">{MEDAL[i] || i + 1}</span>
                <div className="lb-body">
                  <b>{u.name}</b>
                  <small>{u.role || "Member"} · {u.actions} actions · Last: {u.last_at ? new Date(u.last_at).toLocaleString() : "—"}</small>
                  {(u.badges || []).length > 0 && (
                    <div className="lb-badges" data-testid={`lb-badges-${i}`}>
                      {(u.badges || []).map((b) => (
                        <span key={b.id} className={`lb-badge lb-badge-${b.kind}`} title={b.hint || b.label} data-testid={`lb-badge-${i}-${b.id}`}>
                          <span className="lb-badge-emoji">{b.emoji}</span>{b.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="lb-points" data-testid={`lb-points-${i}`}><b>{u.points}</b><em>pts</em></div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {me?.recent?.length > 0 && (
        <section className="panel" style={{ marginTop: 14 }}>
          <h3 style={{ margin: 0, font: "700 14px 'Plus Jakarta Sans'", letterSpacing: ".3px", textTransform: "uppercase", color: "#35655d" }}>
            <Clock size={14} style={{ verticalAlign: "middle" }} /> Your recent activity
          </h3>
          <ul className="lb-recent" data-testid="lb-recent">
            {me.recent.map((r) => (
              <li key={r.log_id} data-testid={`lb-recent-${r.log_id}`}>
                <span className="lb-recent-pts">+{r.points}</span>
                <div>
                  <b>{r.reason_label}</b>
                  <small>{new Date(r.at).toLocaleString()}</small>
                </div>
                <Sparkles size={13} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
